import React from 'react';
import { FaBriefcase, FaCogs, FaMicrochip, FaWarehouse, FaLock, FaCog } from 'react-icons/fa'; // Added new icons
import Navbar from './Navbar';
import Footer from './Footer';
import useAdminCareer from '../Hooks/useAdminCareer';
import { Link } from 'react-router-dom';
import { FaLightbulb } from 'react-icons/fa';
import { FaDollarSign } from 'react-icons/fa';
import { FaMoneyBill } from 'react-icons/fa';
import NavbarWithPushContent from './NavbarWithPushContent';
import LoadingSpinner from '../Assets/LoadingSpinner';
import { FaIndustry } from 'react-icons/fa';
import { FaBalanceScale } from 'react-icons/fa';
import { FaFlask } from 'react-icons/fa';
import { FaCheckCircle } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Reveal from '../Assets/Reveal';
import Seo from '../Assets/Seo';

interface CareerProps {
  id: string;
  title: string;
  shortDescription: string;
  jobType: string;
  category: string;
  icon: React.ReactNode;
}

type ProductCategory = {
  _id: string;
  name: string;
  subcategories: { name: string; products: any[] }[];
  products: any[];
};


interface CareersProps {
  products: ProductCategory[]; // or whatever type your products are (better to replace `any` later!)
  lang: string
}


const CareerCard: React.FC<CareerProps> = ({ id, title, shortDescription, jobType, category, icon }) => {
  const { t } = useTranslation()

  return (
    <div className="group flex h-full flex-col rounded-2xl border border-gray-100 bg-white p-6 shadow-md transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl">
      <div className="mb-4 flex items-center gap-4">
        <div className="flex h-14 w-14 flex-none items-center justify-center rounded-xl bg-brand-50 text-brand-500 transition-colors duration-300 group-hover:bg-brand-400 group-hover:text-white">
          {icon}
        </div>
        <h3 className="text-lg font-bold text-brand-gray leading-snug">{title}</h3>
      </div>

      {category && (
        <span className="mb-3 inline-block w-fit rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-600">
          {category}
        </span>
      )}

      <p className="mb-6 flex-grow text-gray-600">{shortDescription}</p>

      <div className="mt-auto flex items-center justify-between">
        <Link to={`/careers/${id}`}>
          <button className="rounded-lg bg-brand-400 px-5 py-2 font-semibold text-white transition-colors duration-300 hover:bg-brand-500">
            {t('career.applyButton')}
          </button>
        </Link>
        <span className="text-sm text-gray-500">{jobType}</span>
      </div>
    </div>
  );
};

const Careers: React.FC<CareersProps> = ({ products, lang }) => {

  const { t } = useTranslation()
  const { careers, loading, error, setCurrentPage, totalCount } = useAdminCareer(6, "loadMore", true);

  // Filter careers to only those with non-empty vacancy[lang] and shortDescription[lang]
  const filteredCareers = careers.filter(
    (career: any) =>
      career.vacancy && career.vacancy[lang] && career.vacancy[lang].trim() !== "" &&
      career.shortDescription && career.shortDescription[lang] && career.shortDescription[lang].trim() !== ""
  );

  // Function to get the correct icon based on the category
  const getIconForCategory = (department: { en?: string }) => {
    const iconProps = { size: 28 };

    switch (department?.en) {
      case 'Graphic Design':
        return <FaLightbulb {...iconProps} />;
      case 'R&D':
        return <FaCogs {...iconProps} />;
      case 'Pharmaceutical Quality Control':
        return <FaFlask {...iconProps} />;
      case 'Pharmaceutical Quality Assurance':
        return <FaCheckCircle {...iconProps} />;
      case 'Pharmaceutical Microbiology':
        return <FaMicrochip {...iconProps} />;
      case 'Pharmaceutical Regulatory Affairs':
        return <FaBalanceScale {...iconProps} />;
      case 'Maintenance and Engineering':
        return <FaCog {...iconProps} />;
      case 'MANUFACTURE':
        return <FaIndustry {...iconProps} />;
      case 'Storage':
        return <FaWarehouse {...iconProps} />;
      case 'Sales':
        return <FaDollarSign {...iconProps} />;
      case 'Finance':
        return <FaMoneyBill {...iconProps} />;
      case 'SECURITY':
        return <FaLock {...iconProps} />;
      default:
        return <FaBriefcase {...iconProps} />;
    }
  };


  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Seo title={t("seo.careers.title")} description={t("seo.careers.description")} lang={lang} />
      <div className="hidden md:block">
        <NavbarWithPushContent products={products} lang={lang} />
      </div>

      <div className="block md:hidden">
        <Navbar products={products} lang={lang} />
      </div>

      {/* Hero / intro */}
      <section className="bg-gray-100 px-4 pt-28 md:pt-32 pb-14">
        <div className="max-w-6xl mx-auto grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
          <div className="text-center lg:text-left">
            <h1 className="text-3xl sm:text-4xl font-bold text-brand-gray">
              {t('career.header')}
            </h1>
            <span className="mx-auto mt-4 mb-6 block h-1 w-16 rounded-full bg-brand-400 lg:mx-0" />
            <p className="text-lg text-gray-600 leading-relaxed">
              {t('career.text1')}
            </p>
            <p className="mt-4 text-lg text-gray-600 leading-relaxed">
              {t('career.text2')}
            </p>
          </div>

          <div>
            <img
              src="/2/career_50.jpg"
              alt="Career"
              className="w-full rounded-2xl shadow-lg object-cover"
            />
          </div>
        </div>
      </section>

      {/* Open positions */}
      <section className="bg-gray-50 px-4 py-16">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <p className="text-center text-red-500 text-lg">Failed to load careers. Please try again later.</p>
          ) : filteredCareers && filteredCareers.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCareers.map((career: any, index: number) => (
                <Reveal key={index} delay={(index % 3) * 100} className="h-full">
                  <CareerCard
                    id={career._id}
                    title={career.vacancy[lang]}
                    shortDescription={career.shortDescription[lang]}
                    jobType={career.jobType}
                    category={career.department[lang]}
                    icon={getIconForCategory(career.department)}
                  />
                </Reveal>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-600 text-lg mt-12">
              {t('career.noOpenPositions')}
            </p>
          )}

          {careers.length < totalCount && (
            <div className="text-center mt-10">
              <button
                onClick={() => setCurrentPage((prev) => prev + 1)}
                className="rounded-lg bg-brand-400 px-6 py-3 font-semibold text-white transition-colors duration-300 hover:bg-brand-500 disabled:opacity-60"
                disabled={loading}
              >
                {loading ? "Loading..." : "Load More"}
              </button>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Careers;
